import { CacheMap } from '@ns/cache';
import { logCoalesce } from './debug-events';
import { traceIdentityOf } from './trace';
import type {
  CachedResponse,
  CoalesceCacheSettings,
  CoalesceOptions,
  FetchFunction,
  Fingerprint,
  ObservedResponse,
  RequestOptionsWithContext,
  RequestOptionsWithoutContext,
  UntracedResult,
} from './types';

/**
 * Layer 6: two callers asking the same question at the same time become ONE
 * request on the wire, and both get the answer.
 *
 * It sits INSIDE the retry loop and around the exchange, which is what decides
 * what a fingerprint has to carry. Everything above it — the `before` handlers,
 * the policies, the retry loop and the delivery choice — runs per caller and
 * therefore cannot be shared by accident (RY11, PO10, TE11).
 */

/** The decoded options, which are the only ones that can reach this file. */
type DecodedOptions = RequestOptionsWithContext | RequestOptionsWithoutContext;

/**
 * What the exchange produces: the answer as observed, and the result built from
 * it. Both are here because both are handed to every caller that joined —
 * `ObservedResponse` reaches the `after` handlers, the result reaches the
 * caller — and a copy has to cover both or the sharing simply moves.
 */
export interface Exchange {
  observed: ObservedResponse;
  result: UntracedResult<unknown>;
  /**
   * The headers this flight actually handed the transport (`TR28`).
   *
   * Here rather than beside `requestId` on the flight, because this object only
   * exists once the transport has answered. A caller that joined and receives
   * one therefore knows something went out, and can report the trace of the
   * request that really happened instead of one it prepared and never sent.
   *
   * Required, so a producer cannot leave it behind: `copyOf` below rebuilds an
   * exchange field by field, and a field that may be absent is one a rebuild
   * silently drops.
   */
  sent: Headers;
}

/**
 * Where a flight keeps its answer, and what a later call is checked against
 * before it may reuse it.
 */
export interface FlightCache {
  /** The headers this call goes out with, as `headersTokenOf` writes them. */
  readonly headersToken: string;
  /**
   * Who this call is on the wire, beyond its headers: the resolved transport
   * by identity and the `credentials` option. A per-user `fetch` carries a
   * token the headers never show, so a kept answer is bound to both.
   */
  readonly identityToken: string;
  /** The key in `store`: the `fingerprint` string, or the whole flight key. */
  readonly key: string;
  readonly maxResponseBytes: number | undefined;
  readonly store: CacheMap<CachedResponse>;
  /** The inline `ttl`, or `undefined` for a store of the caller's own. */
  readonly ttl: number | undefined;
}

/** What one caller needs in order to join: which pool, and its own copy or not. */
export interface Flight {
  /** Present only under `coalesce.cache`, so a call without it computes nothing. */
  readonly cache?: FlightCache;
  readonly clone: boolean;
  readonly key: string;
}

/** What boarding gives back: this caller's copy, and the flight's own id. */
export interface Joined {
  exchange: Promise<Exchange>;
  /**
   * The id the request on this flight carries. For a caller that JOINED this is
   * the opener's and not its own, because only one of the two reached the wire
   * (ER64, ER65).
   */
  requestId: string | undefined;
  /**
   * The same id, but only once the flight has handed a request to the transport.
   *
   * A question and not a value, because the answer moves after boarding while
   * `requestId` above is fixed at it: that one is the id the opener PREPARED,
   * and preparing an id is not sending it. Ask this where a flight FAILED,
   * since there is no `Exchange` then and so no `sent` to read the wire off,
   * and an id reported without one would name a request nobody can look up.
   *
   * Today it answers yes for every flight there is, because the work reaches
   * its send point before it first awaits and a flight only exists once that
   * work has been started. What this buys is that the answer keeps tracking the
   * send point if a step is ever added ahead of it.
   */
  sentRequestId: () => string | undefined;
}

/**
 * The facts the default fingerprint reads off the call rather than off the
 * options: the url after `baseUrl` and `params` were folded in (CO20, CO21),
 * the headers after the global ones and the `before` handlers had their say
 * (CO14, CO34, CO36), and the transport the call actually resolved to.
 *
 * The transport is here and not read from `options.fetch` because a call
 * without one still HAS a transport: it falls through to `request.fetch` and
 * then to the platform (FE18 to FE21). Keying on the option would give two
 * callers who resolved to DIFFERENT process-wide transports the same key, and
 * the second would be answered by a transport it was never pointed at (CO58).
 */
export interface WireFacts {
  headers: Headers;
  method: string;
  /**
   * The trace headers this call WOULD send, unread unless `perTrace` asks.
   *
   * Carried rather than read off `headers`, because the headers are not on the
   * request yet: they go on after the key is built, so that a trace cannot
   * split a pool by accident (TR14). And carried raw rather than as a finished
   * key, so that `traceIdentityOf` runs only for the calls that ask -- which is
   * what keeps this type's promise that a call which did not ask computes
   * nothing (CO1).
   */
  traced: Record<string, string>;
  /**
   * Whether those headers CONTINUE somebody else's trace rather than starting
   * one here. A self-minted request id is the `callId`, so it may not count as
   * a trace (TR15).
   */
  tracedIsInherited: boolean;
  transport: FetchFunction | undefined;
  url: string;
}

/** The flights in the air, shared by the whole process. */
const flights = new Map<string, Airborne>();

/**
 * A stable id per REFERENCE, held weakly so nothing here keeps an object alive.
 *
 * DECISION 9.12. A fingerprint that asked "is there a custom fetch" instead
 * would give two callers with their OWN transport — one holding Alice's token,
 * one Bob's — the same key and one shared flight. An id per reference splits
 * them, and a caller who builds a fresh transport per call simply gets a pool of
 * one: no coalescing, no leak.
 */
const identities = new WeakMap<object, string>();
let identityCount = 0;

function identityOf(value: object): string {
  const known = identities.get(value);
  if (known !== undefined) return known;
  identityCount += 1;
  const fresh = String(identityCount);
  identities.set(value, fresh);
  return fresh;
}

/**
 * One ingredient as a string.
 *
 * A reference is keyed by identity rather than by shape, and that is the safe
 * direction: two structurally identical readers get their own pool, while two
 * that only LOOK alike can never be merged into one (CO29).
 */
function tokenOf(value: unknown): string {
  // One case per `typeof` result, so each branch narrows `value` and the switch
  // is exhaustive without a default.
  switch (typeof value) {
    case 'undefined':
      return 'undefined';
    case 'object':
      // `null` is reachable (a `null` body is a JSON value), but not
      // independently testable: the only option that can be `null` is the
      // body, and the body is already an ingredient of this fingerprint in its
      // serialized form. So two callers that differ here differ twice over, and
      // changing this token alone moves nothing a caller can see. MEASURED
      // 2026-08-26 while chasing it as a coverage gap.
      return value === null ? 'null' : `ref:${identityOf(value)}`;
    case 'function':
      return `ref:${identityOf(value)}`;
    case 'string':
    case 'number':
    case 'boolean':
    case 'bigint':
    case 'symbol':
      return `${typeof value}:${String(value)}`;
  }
}

/** Every header the call goes out with. `Headers` iterates sorted and folded. */
function headersTokenOf(headers: Headers): string {
  const entries: string[][] = [];
  headers.forEach((value, name) => entries.push([name, value]));
  return JSON.stringify(entries);
}

/**
 * Which options make two calls the same call.
 *
 * The rule is the asymmetry: an ingredient too many splits a pool that could
 * have been shared and costs a saving; an ingredient too few merges two pools
 * that must not merge and hands one caller another caller's data. So everything
 * that is not PROVABLY unable to change what goes on the wire is IN.
 *
 * Eight are out, and each one has its reason. `retry`, `policies` and
 * `throwOnError` run on layers 4, 3 and the outermost one, where nothing can be
 * shared by accident (RY11, PO10, TE11). `validate.response` runs per caller on
 * that caller's own copy, outside the shared region (VA9, VA10, CO16).
 * `context` and `isolate` reach the wire only through the headers, and the
 * headers are here (CX8, CO17, CO34) — so with no process-wide headers set, an
 * isolated call and an ordinary one send a byte-identical request and MUST
 * share a flight. `before` and `after` are functions, and a function is not
 * what goes out: what a `before` ADDS is here already, because the key is
 * computed on `final` (CO14, CO38), and an `after` never touches the wire at
 * all (CO39). Keeping either would split two callers sending byte-for-byte the
 * same request, and `after` is the dearest of the two — an inline
 * `after: () => log()` is a fresh function on every call, so such a caller
 * would never coalesce with anyone, itself included.
 *
 * `coalesce` itself is not an ingredient: the MODE already is the pool, see
 * `flightOf`. `raw` is not one either, because no raw call reaches this file.
 */
function defaultKeyOf(options: DecodedOptions, wire: WireFacts): string {
  return `d:${JSON.stringify([
    wire.url,
    wire.method,
    headersTokenOf(wire.headers),
    // The value BEFORE serialization, because the key is computed one line
    // before the serializer runs (CO22).
    tokenOf(options.body),
    // The RESOLVED transport, not `options.fetch`. A call that gave none still
    // goes out on one, and which one is a fact about this call (CO25, CO58).
    tokenOf(wire.transport),
    tokenOf(options.serializer),
    tokenOf(options.deserializer),
    tokenOf(options.cache),
    tokenOf(options.credentials),
    tokenOf(options.integrity),
    tokenOf(options.keepalive),
    tokenOf(options.mode),
    tokenOf(options.priority),
    tokenOf(options.redirect),
    tokenOf(options.referrer),
    tokenOf(options.referrerPolicy),
    // `timeout` is here and `signal` is NOT, and the two used to sit together.
    //
    // A shared flight has ONE lifetime, so two callers with different budgets
    // would share a budget belonging to neither -- that is what `timeout` keeps
    // apart. The signal was here for a different reason: to stop a departing
    // caller from taking the flight down. That protection moved to the
    // participant count (CO43, CO44), which aborts only once EVERY participant
    // has left, and does it in the place that can tell who is still listening.
    //
    // Keeping both was a second lock on a door already locked, and it cost the
    // whole feature: every caller that cleans up after itself passes a signal,
    // so default coalescing quietly did nothing (CO15, CO56).
    tokenOf(options.timeout),
    tokenOf(options.maxResponseBytes),
  ])}`;
}

/**
 * A named pool. The name is the promise the caller makes by writing one: this
 * data is the same for everyone (CO3).
 *
 * @remarks
 * The name is the identity with ONE exception, `maxResponseBytes`. A cap is not
 * a statement about the data being the same, it is this caller's own ceiling,
 * and a joiner that inherited the opener's cap would silently run without the
 * one it wrote (CO71). The default pool has always keyed on it (CO30); this is
 * the named pool catching up rather than a new rule.
 *
 * Folded in STRUCTURALLY for the reason `perTraceKeyOf` gives: a name is caller
 * text, so a suffix is forgeable and `JSON.stringify` delimits its members.
 */
function namedKeyOf(name: string, options: DecodedOptions): string {
  return `n${JSON.stringify([name, tokenOf(options.maxResponseBytes)])}`;
}

/**
 * A computed pool, and the FUNCTION REFERENCE is part of it (DECISION 9.4).
 *
 * Two separate computations that happen to produce the same string still get
 * their own pool (CO4), and so do a named pool and a computed one that agree on
 * the name (CO8). The alternative was letting whoever opened the flight impose
 * their computation on everyone behind them (CO9), which only shows when two
 * callers who never met overlap in time.
 *
 * @remarks
 * `maxResponseBytes` is keyed on beside the computation, for the reason the
 * named pool gives (CO72): a cap is this caller's own ceiling rather than a
 * statement about the data, and a fingerprint function is under no obligation
 * to mention it. The reference stays OUTSIDE the delimited part, so a
 * computation returning JSON-looking text cannot reach another reference's
 * pool.
 */
function computedKeyOf(
  fingerprint: Fingerprint,
  options: DecodedOptions,
): string {
  return `f${identityOf(fingerprint)}${JSON.stringify([
    fingerprint(options),
    tokenOf(options.maxResponseBytes),
  ])}`;
}

/**
 * The three shorthands, written as the object form they stand for.
 *
 * `true` says "pool me, no fingerprint of my own"; a name or a function says
 * "pool me on THIS". Saying that once is what keeps the `clone` default in a
 * single place, rather than spelled out per shorthand where three of the four
 * spellings could be flipped to `false` without a test noticing (CO6).
 *
 * The return type is DERIVED from `CoalesceOptions` rather than restated. A
 * hand-written copy accepts a wider value from `return mode` without complaint,
 * so the next option added to the source type would simply never arrive here,
 * and nothing would say so.
 */
function toObjectForm(
  mode: Exclude<CoalesceOptions, false | undefined>,
): Exclude<CoalesceOptions, boolean | string | Fingerprint | undefined> {
  // Mutating this comparison survives, and that is correct rather than a gap.
  // `mode` cannot be `false` here (the parameter type excludes it), so a mutant
  // reading `mode === false` lets `true` fall through to `return mode`. Every
  // reader downstream asks for a property, and `true.fingerprint` and
  // `({}).fingerprint` are both `undefined`, so nothing a caller can see moves.
  // MEASURED 2026-09-01, same family as the `null` token below.
  if (mode === true) return {};
  if (typeof mode === 'string' || typeof mode === 'function')
    return { fingerprint: mode };
  return mode;
}

/**
 * Which pool this call joins, or `undefined` when it joins none.
 *
 * Nothing is computed for a call that did not ask, which is what makes the
 * default cost nothing at all (CO1).
 */
export function flightOf(
  options: DecodedOptions,
  wire: WireFacts,
): Flight | undefined {
  const coalesceMode = options.coalesce;
  if (coalesceMode === undefined || coalesceMode === false) return undefined;

  const {
    cache,
    clone = true,
    fingerprint,
    perTrace = false,
  } = toObjectForm(coalesceMode);
  // "What is this pool called" and "between whom is it shared" are separate
  // questions, so the trace applies to EVERY naming strategy rather than living
  // inside one of them. Keeping it inside `defaultKeyOf` was what made
  // `{ fingerprint: 'users', perTrace: true }` share one request across two
  // traces (TR15).
  //
  // Folded in STRUCTURALLY, not appended. A name is caller text and so is a
  // computed fingerprint, so a suffix is forgeable: MEASURED,
  // `{ fingerprint: 'users|', perTrace: true }` and `{ fingerprint: 'users|||' }`
  // produced one key, and the second caller was answered with the first one's
  // body. A JSON array delimits its members, and an unwrapped key starts with
  // `d`, `n` or `f` where a wrapped one starts with `[`, so the two spaces
  // cannot meet either (CO3, CO4).
  const perTraceKeyOf = (base: string): string =>
    perTrace
      ? JSON.stringify([
          base,
          traceIdentityOf(wire.traced, wire.tracedIsInherited),
        ])
      : base;

  // The cache settings go in BEFORE the trace, so the trace stays the outer
  // layer it is everywhere else, and they go in structurally for the same
  // reason the trace does.
  const key = perTraceKeyOf(
    withCacheOf(baseKeyOf(fingerprint, options, wire), cache),
  );
  if (cache === undefined) return { clone, key };

  const checkedAgainst = {
    headersToken: headersTokenOf(wire.headers),
    // The same two tokens the default key carries (CO25, CO18), so a kept
    // answer under a fingerprint is bound to them as well.
    identityToken: JSON.stringify([
      tokenOf(wire.transport),
      tokenOf(options.credentials),
    ]),
    maxResponseBytes: options.maxResponseBytes,
  };
  if (!isOwnStore(cache)) {
    return {
      cache: {
        ...checkedAgainst,
        key,
        store: inlineStoreOf(cache),
        ttl: cache.ttl,
      },
      clone,
      key,
    };
  }
  // A string the caller wrote is a key the caller can `delete`. Any other key
  // is an internal one, and a trace in it may never be dropped: two traces
  // would then share what `perTrace` promised to keep apart.
  const cacheKey =
    typeof fingerprint === 'string' && !perTrace ? fingerprint : key;
  return {
    cache: { ...checkedAgainst, key: cacheKey, store: cache, ttl: undefined },
    clone,
    key,
  };
}

/** The pool name before the cache settings and the trace are folded in. */
function baseKeyOf(
  fingerprint: Fingerprint | string | undefined,
  options: DecodedOptions,
  wire: WireFacts,
): string {
  if (fingerprint === undefined) return defaultKeyOf(options, wire);
  if (typeof fingerprint === 'string') return namedKeyOf(fingerprint, options);
  return computedKeyOf(fingerprint, options);
}

/**
 * The key with the cache settings in it, so two calls with other settings, or
 * another store, share nothing: whose `maxEntries` would hold otherwise?
 */
function withCacheOf(
  base: string,
  cache: CacheMap<CachedResponse> | CoalesceCacheSettings | undefined,
): string {
  if (cache === undefined) return base;
  return JSON.stringify([
    base,
    isOwnStore(cache)
      ? ['cache', tokenOf(cache)]
      : ['cache', tokenOf(cache.ttl), tokenOf(cache.maxEntries)],
  ]);
}

/**
 * The caller's own `CacheMap`, told apart by its `set` rather than by
 * `instanceof`: two copies of `@ns/cache` in one process are two classes.
 */
function isOwnStore(
  cache: CacheMap<CachedResponse> | CoalesceCacheSettings,
): cache is CacheMap<CachedResponse> {
  return 'set' in cache && typeof cache.set === 'function';
}

/** The library's own stores, one per `maxEntries` value, shared by the process. */
const inlineStores = new Map<number | 'unbounded', CacheMap<CachedResponse>>();

/**
 * The inline store for these settings. It has no `ttl` of its own: every
 * answer carries the `ttl` of the call that kept it.
 */
function inlineStoreOf(
  settings: CoalesceCacheSettings,
): CacheMap<CachedResponse> {
  const bound = settings.maxEntries ?? 'unbounded';
  const known = inlineStores.get(bound);
  if (known !== undefined) return known;
  const fresh = new CacheMap<CachedResponse>({
    maxEntries: settings.maxEntries,
    name: `http-request coalesce (${bound})`,
  });
  inlineStores.set(bound, fresh);
  return fresh;
}

/**
 * This caller's own copy of the shared answer.
 *
 * `structuredClone` rather than a JSON round-trip, which is not an alternative:
 * a `Date` comes back a string and a `Map` comes back `{}`. MEASURED at 1.1 to
 * 1.7 ms on a 5000-item body, against the tens of milliseconds the call itself
 * costs (CO13).
 *
 * The result is rebuilt as a DECODED one rather than spread, and that is the
 * type saying what is already true: a stream is single-consumer, so no raw call
 * can coalesce (CO12) and no raw result can reach this line.
 *
 * One `Headers` for both views, the way the uncopied pair share the response's.
 */
function copyOf(shared: Exchange): Exchange {
  return { ...answerCopyOf(shared), sent: new Headers(shared.sent) };
}

/** The answer half of {@link copyOf}: everything but what went out. */
function answerCopyOf(shared: Answer): Answer {
  const headers = new Headers(shared.result.headers);
  return {
    observed: {
      headers,
      ok: shared.observed.ok,
      status: shared.observed.status,
      statusText: shared.observed.statusText,
    },
    result: {
      body: structuredClone(shared.result.body),
      headers,
      ok: shared.observed.ok,
      status: shared.result.status,
      statusText: shared.result.statusText,
    },
  };
}

/** An exchange without the headers that went out, which a kept answer drops. */
type Answer = Omit<Exchange, 'sent'>;

/** What a {@link CachedResponse} stands for. */
interface KeptAnswer {
  readonly answer: Answer;
  readonly headersToken: string;
  readonly identityToken: string;
  readonly maxResponseBytes: number | undefined;
}

/**
 * The content behind every `CachedResponse` this process made.
 *
 * A side table rather than fields on the value, so the value a caller holds in
 * its own `CacheMap` shows nothing, and a value from anywhere else has no entry
 * here and reads as a miss.
 */
const keptAnswers = new WeakMap<object, KeptAnswer>();

function isCachedResponse(value: object): value is CachedResponse {
  return keptAnswers.has(value);
}

/**
 * The value to keep for this answer, or `undefined` when the answer cannot be
 * copied. A body `structuredClone` refuses fails every caller's own copy as it
 * is, so not keeping it changes nothing they see.
 */
function cachedResponseOf(
  settled: Exchange,
  cache: FlightCache,
): CachedResponse | undefined {
  let answer: Answer;
  try {
    answer = answerCopyOf(settled);
  } catch {
    return undefined;
  }
  const token = Object.freeze({});
  keptAnswers.set(token, {
    answer,
    headersToken: cache.headersToken,
    identityToken: cache.identityToken,
    maxResponseBytes: cache.maxResponseBytes,
  });
  return isCachedResponse(token) ? token : undefined;
}

/**
 * The kept answer for this call, as a copy of its own, or `undefined` on a miss.
 *
 * `has` before `get`, so a miss reports nothing to a store's observers: the
 * miss is in the debug log as `opened`. An answer kept for other request
 * headers, on another transport, with other `credentials` or under another
 * `maxResponseBytes` is a miss as well. That is what
 * keeps a `fingerprint`, which shares a FLIGHT between tokens, from sharing a
 * kept answer between them.
 *
 * Synchronous, because `request` asks it on the way to `joinFlight`, and an
 * await there lets a joiner miss a flight (CO68).
 *
 * A hit is not a request: `sent` is empty, and the call reports nothing about
 * a wire it never used.
 */
export function cachedExchangeOf(flight: Flight): Exchange | undefined {
  const { cache } = flight;
  if (!cache?.store.has(cache.key)) return undefined;
  const value = cache.store.get(cache.key);
  const kept = value === undefined ? undefined : keptAnswers.get(value);
  if (
    kept?.headersToken !== cache.headersToken ||
    kept.identityToken !== cache.identityToken ||
    kept.maxResponseBytes !== cache.maxResponseBytes
  ) {
    return undefined;
  }
  logCoalesce('hit', cache.key);
  return { ...answerCopyOf(kept.answer), sent: new Headers() };
}

/**
 * Who is flying under one cache key: the number of the newest flight that
 * opened, and how many are still in the air. Per store, held weakly, and an
 * entry leaves when its last flight releases, so this grows with the flights
 * in the air and not with the keys ever used.
 */
interface Running {
  inFlight: number;
  newest: number;
}

const runningPerStore = new WeakMap<
  CacheMap<CachedResponse>,
  Map<string, Running>
>();
let flightCount = 0;

/** Signs a flight in under its cache key, and returns its number. */
function signIn(cache: FlightCache): number {
  flightCount += 1;
  const perKey = runningPerStore.get(cache.store) ?? new Map<string, Running>();
  runningPerStore.set(cache.store, perKey);
  const running = perKey.get(cache.key) ?? { inFlight: 0, newest: 0 };
  running.inFlight += 1;
  running.newest = flightCount;
  perKey.set(cache.key, running);
  return flightCount;
}

/** Signs a flight out. Called once per flight, from its one release. */
function signOut(cache: FlightCache): void {
  const perKey = runningPerStore.get(cache.store);
  const running = perKey?.get(cache.key);
  if (perKey === undefined || running === undefined) return;
  running.inFlight -= 1;
  if (running.inFlight === 0) perKey.delete(cache.key);
}

/**
 * Keeps a settled answer, when this flight may: it is `ok`, and no flight
 * opened under the same cache key after this one. The last opener wins, so an
 * older flight that settles late never overwrites a newer answer.
 *
 * Nothing here ever removes an entry, so a value the caller wrote with `set`
 * stays until a flight that may keep replaces it.
 */
function keepAnswer(
  cache: FlightCache,
  number: number,
  settled: Exchange,
): void {
  if (!settled.observed.ok) return;
  if (runningPerStore.get(cache.store)?.get(cache.key)?.newest !== number) {
    return;
  }
  const value = cachedResponseOf(settled, cache);
  if (value !== undefined)
    cache.store.set(cache.key, value, { ttl: cache.ttl });
}

/**
 * How many flights are signed in under each cache key of `store`. For tests
 * only: a flight that never signs out shows here.
 */
export function runningFlightsOf(store: CacheMap<CachedResponse>): number {
  return runningPerStore.get(store)?.size ?? 0;
}

/**
 * One flight in the air: the work, who is still waiting for it, and its own
 * place in the table.
 *
 * All three are one thing because they are one lifetime. A participant leaving
 * ends THAT participant's call and nothing else; the shared work is cut off
 * only once EVERY participant has left (CO43, CO44), and the key leaves the
 * table at that same moment (CO45). With a single participant those coincide,
 * so that is the same rule rather than a special case. Letting the count and
 * the work be one thing is what used to hand the opener's abort to everybody
 * who had joined behind them.
 *
 * The key is held from IN HERE rather than handed a callback to fire, because
 * an object that decides when a flight is over is the only one that can know
 * when its entry is stale. Reaching sideways into the table from the count was
 * the same knowledge split across two owners.
 *
 * `AbortSignal.any` cannot express the count. It fires on the FIRST source
 * where this needs the LAST, and the set of sources grows while the flight is
 * already running. So it is a COUNT, not a composition of signals — and it
 * lives with the controller it decides over, so that a caller of `join` has
 * nothing left to get wrong.
 *
 * A real `AbortController` rather than a stand-in. MEASURED: `AbortSignal` has
 * no constructor to extend (`new AbortSignal()` throws `Illegal constructor`)
 * and its static factories hand back a plain one regardless, so the only route
 * left would be reassigning a prototype — a price nothing here needs to pay.
 */
interface Airborne {
  /**
   * Boards one participant, and hands back that participant's own view of the
   * flight.
   *
   * The signal handed in is the participant's MERGED one, the caller's own
   * cancellation and its deadline as a single object (`startDeadline`), because
   * both of those are departures: a caller that gives up and a clock that runs
   * out are the same event seen from the flight (CO46, CO47).
   */
  readonly join: (
    participant: AbortSignal,
    clone: boolean,
  ) => Promise<Exchange>;
  /**
   * The id the request on this flight actually carries, which is the opener's:
   * it is the only one that reached the wire, so it is the only one anybody on
   * this flight may report (ER64).
   */
  readonly requestId: string | undefined;
  /**
   * That id once the work has reached the transport, and nothing before then.
   *
   * A flight cannot see its own send point -- that lives inside `open`, several
   * layers up -- so `open` is handed the mark to set when it gets there. Held
   * apart from `requestId` above because the two answer different questions:
   * what the opener prepared, and what actually went out.
   */
  readonly sentRequestId: () => string | undefined;
}

/**
 * Opens one flight: the work, the count over it, and the key it answers to.
 *
 * `releaseKey` runs BEFORE the abort, and that ordering is the whole of CO49:
 * the key has to be off the table by the moment anybody can look, not once the
 * abandoned exchange has finally settled. Between those two lies a window in
 * which a fresh caller would be handed a flight that is already dying, and
 * inherit an abort it never asked for.
 *
 * `open` is handed the signal to run on rather than closing over one, which is
 * where the two cancellations part company: the FLIGHT's belongs to this file
 * and the CALLER's stays with `request`. While they were one signal, whoever
 * happened to open the flight cancelled it for everybody behind them.
 */
function createAirborne(
  releaseKey: () => void,
  open: (signal: AbortSignal, reachedWire: () => void) => Promise<Exchange>,
  requestId: string | undefined,
  keep?: (settled: Exchange) => void,
): Airborne {
  const ours = new AbortController();
  let participants = 0;
  let released = false;
  /**
   * Whether the work got as far as handing a request to the transport.
   *
   * The moment `open` is CALLED is not that moment, which is why this is a mark
   * `open` sets rather than something read off the call below. Today the two
   * happen to coincide, because the work reaches its send point before it first
   * awaits; a step added ahead of that send point would part them, and then a
   * flight would be claiming a request it had not made yet.
   */
  let onTheWire = false;

  /**
   * ONCE, because a flight has two ways to lose its key — the last participant
   * walking away and the exchange settling — and a later flight may hold the
   * key by the time the second one runs. It can only do so BECAUSE the first
   * one ran, since a key is claimed only when the table has none, so this one
   * flag is the whole guard: a second release would take a living flight down
   * with a dead one.
   */
  const release = (): void => {
    if (released) return;
    released = true;
    releaseKey();
  };

  /**
   * One participant gone, and the decision that goes with it.
   *
   * The reason travels from whoever left LAST, which is the case a single
   * participant makes ordinary: alone on a flight, its own cancellation reaches
   * the transport unchanged, exactly as it did when there was no count at all.
   */
  const cancelWhenEmpty = (reason: unknown): void => {
    if (participants !== 0) return;
    release();
    ours.abort(reason);
  };

  const exchange = open(ours.signal, () => {
    onTheWire = true;
  }).then(
    settled => {
      // Kept BEFORE the release, so a call that starts right after finds the
      // answer rather than an empty table. Not kept once released: then every
      // participant walked away first, and nobody asked for this answer any
      // more.
      if (!released) keep?.(settled);
      release();
      return settled;
    },
    (error: unknown) => {
      release();
      throw error;
    },
  );

  return {
    join: (participant, clone) => {
      participants += 1;
      /**
       * CLEARED on departure, not merely flagged, and that nullable reference
       * IS the guard against leaving twice. A flag would leave this closure
       * holding the departed participant's merged signal, and the closure is
       * reachable from the shared exchange — so a flight whose transport
       * ignores the abort and never settles would keep every caller that ever
       * gave up alive for the life of the process.
       */
      let signal: AbortSignal | undefined = participant;

      const leave = (): void => {
        const departing = signal;
        if (departing === undefined) return;
        signal = undefined;
        // Detached here and not only on abort, for the same reason
        // `deadline.stop` releases its merge: one long-lived `AbortController`
        // in a component outlives every call made under it, and a listener left
        // behind on it is flight state that never goes away.
        departing.removeEventListener('abort', leave);
        participants -= 1;
        cancelWhenEmpty(departing.reason);
      };

      // An already-aborted signal fires no event, so the listener alone would
      // keep counting a participant that was gone before it arrived.
      //
      // NOT THE SAME CHECK the pipeline already did before it got here. That one
      // reads the caller's own `signal`; what arrives here is the MERGE of that
      // signal with the call's deadline. A timeout that runs out during the
      // `before` handlers, the request validator or the body serialization
      // aborts the merged one while the caller's is still clean, so a
      // participant can arrive already gone. Without this line the count would
      // only drop where the flight settles, which keeps the shared work running
      // for someone who is no longer waiting -- and never drops at all on a
      // transport that ignores the abort.
      if (participant.aborted) leave();
      else participant.addEventListener('abort', leave, { once: true });

      // `leave` also fires when the shared work SETTLES, and by then there is
      // nothing left to cancel: the transport has answered and the body has
      // been read inside the exchange. The count still reaches zero there and
      // the controller is still aborted, and that is the same rule applied
      // honestly -- everyone HAS left (CO48). What the call buys is the detach.
      // Two branches rather than `.finally`, which reads better but costs two
      // extra microtask ticks per hand-out: `.finally` builds an intermediate
      // promise and adopts the result through it. Both are correct; on the
      // hottest path in the file the shorter one wins.
      const own = exchange.then(
        settled => {
          leave();
          return settled;
        },
        (error: unknown) => {
          leave();
          throw error;
        },
      );
      return clone ? own.then(copyOf) : own;
    },
    requestId,
    sentRequestId: () => (onTheWire ? requestId : undefined),
  };
}

/**
 * Joins the flight in this pool, or opens one and lets the others join it.
 *
 * THE KEY LEAVES THE TABLE WHEN THE FLIGHT SETTLES, on success and on failure
 * alike (CO7, CO11), and ALSO the moment the last participant walks away
 * (CO45). A failed flight left behind would serve its failure to every later
 * caller forever, and a successful one would turn a request library into a cache
 * nobody asked for. Both removals are the flight's own doing; all this function
 * lends it is which key it answers to.
 *
 * The removal is registered on the promise BEFORE any caller awaits it, so it
 * has run by the time a caller is resumed: a call started right after an
 * `await` finds an empty table and starts over.
 */
export function joinFlight(
  flight: Flight,
  participant: AbortSignal,
  open: (
    transportSignal: AbortSignal,
    reachedWire: () => void,
  ) => Promise<Exchange>,
  requestId: string | undefined,
): Joined {
  const running = flights.get(flight.key);
  logCoalesce(running === undefined ? 'opened' : 'joined', flight.key);
  // A joiner adopts the OPENER's id, because that is the one the server saw.
  // Reporting its own would name a request that exists in no log anywhere,
  // and the field exists precisely to be found back in one (ER64).
  if (running !== undefined)
    return {
      exchange: running.join(participant, flight.clone),
      requestId: running.requestId,
      sentRequestId: running.sentRequestId,
    };

  // Releasing removes THIS flight, not whatever the key happens to hold.
  //
  // A plain delete by key trusts that the only flight ever asking is the one that
  // owns the entry. That trust is what the `released` flag inside the flight is
  // there to make good, and the flag says so itself: a flight has two ways to
  // lose its key, and a later flight may hold it by the time the second one runs.
  //
  // MEASURED, and this is the whole reason to prefer the shape over the flag: a
  // second release is reached in 102 tests across 15 suites (by making it throw),
  // while deleting the flag leaves all 1066 green. So the flag carries a case the
  // suite does not watch, and a guard nothing can make red is a guard on trust.
  //
  // NOT measured, and worth saying rather than implying: whether a release ever
  // actually finds ANOTHER flight's entry under its key. Instrumenting that gave
  // contradictory counts, so the reachability is open. This is written for the
  // case regardless, because the cost is one lookup and the alternative is a
  // guarantee resting on two things staying in step.
  const { cache } = flight;
  const number = cache === undefined ? 0 : signIn(cache);
  const airborne = createAirborne(
    () => {
      if (flights.get(flight.key) === airborne) flights.delete(flight.key);
      // Here, in the one release a flight has, so a late settle after the
      // flight was given up cannot sign out a second time.
      if (cache !== undefined) signOut(cache);
    },
    open,
    requestId,
    cache === undefined
      ? undefined
      : settled => keepAnswer(cache, number, settled),
  );
  flights.set(flight.key, airborne);

  // The opener boards LAST, once the entry it may have to remove exists. Nothing
  // runs in between: an opener whose signal had already fired would otherwise
  // cancel a flight that is about to be written to the table behind it, and the
  // key would outlive the work.
  return {
    exchange: airborne.join(participant, flight.clone),
    requestId,
    sentRequestId: airborne.sentRequestId,
  };
}
